import { ErrorType, PaymentError } from '../error';
import { dateReviver } from '@lialo/common/lib/date';
import {
  BraintreeClientToken,
  CheckoutPayload,
  CheckoutResponse,
  PurchaseUnlockCouponResponse,
  PurchaseCouponPayload
} from "../models/checkout";
import { ProductOwner } from '../models/productOwner';
import { Product } from '../models/product';
import { DonationPayload } from '../models/donation';
import { RedeemRestorationCodePayload, RedeemUnlockCodePayload, RedemptionResponse } from '../models/redemption';
import {
  CodeInfo,
  DetectCodeTypeRequest,
  DiscountCodeInfo,
  UnlockCodeInfo,
  ValidateDiscountCouponRequest,
  ValidateUnlockCodeRequest,
} from '../models/code-info';
import { FetchSettlementListRequest, SettlementListItem } from '../models/settlements';
import { Pagination } from '@lialo/common/lib/pagination';

export class PaymentApi {
  constructor(
    private readonly baseUrl: string,
    private readonly apiContractVersion: number,
    private readonly getIdToken: () => Promise<string | null>,
    private readonly onVersionError: (error: Error) => void
  ) {}

  public requestClientToken(): Promise<BraintreeClientToken> {
    return this.authenticatedRequest('POST', '/api/payment/protected/braintree/client-token');
  }

  public checkout(payload: CheckoutPayload): Promise<CheckoutResponse> {
    return this.authenticatedRequest('POST', '/api/payment/protected/product-order/purchase', undefined, payload);
  }

  public purchaseCoupon(payload: PurchaseCouponPayload): Promise<PurchaseUnlockCouponResponse> {
    return this.authenticatedRequest('POST', '/api/payment/protected/purchase-unlock-coupon', undefined, payload);
  }

  public async donate(payload: DonationPayload): Promise<void> {
    await this.authenticatedRequest('POST', '/api/payment/protected/donation', undefined, payload);
  }

  public fetchProductOwner(): Promise<ProductOwner> {
    return this.authenticatedRequest('GET', '/api/payment/protected/product-owner/me');
  }

  public updateProductOwner(productOwner: ProductOwner): Promise<ProductOwner> {
    return this.authenticatedRequest('PUT', '/api/payment/protected/product-owner/me', undefined, productOwner);
  }

  public fetchProduct(shortLink: string): Promise<Product> {
    return this.authenticatedRequest('GET', `/api/payment/protected/product/${shortLink}`);
  }

  public async redeemRestorationCode(payload: RedeemRestorationCodePayload): Promise<RedemptionResponse> {
    return this.authenticatedRequest('POST', '/api/payment/protected/product-order/restoration', undefined, payload);
  }

  public async redeemUnlockCode(payload: RedeemUnlockCodePayload): Promise<RedemptionResponse> {
    return this.authenticatedRequest('POST', '/api/payment/protected/product-order/unlock', undefined, payload);
  }

  public async detectCodeType(request: DetectCodeTypeRequest): Promise<CodeInfo> {
    return this.authenticatedRequest('GET', '/api/payment/protected/detect-code', request);
  }

  public async validateUnlockCode(request: ValidateUnlockCodeRequest): Promise<UnlockCodeInfo> {
    return this.authenticatedRequest('GET', '/api/payment/protected/product-order/unlock/validate', request);
  }

  public async validateDiscountCode(request: ValidateDiscountCouponRequest): Promise<DiscountCodeInfo> {
    return this.authenticatedRequest('GET', '/api/payment/protected/product-order/discount-coupon/validate', request);
  }

  public updateProduct(product: Product): Promise<Product> {
    return this.authenticatedRequest(
      'PATCH',
      `/api/payment/protected/product/${product.shortLink}`,
      undefined,
      product
    );
  }

  public fetchSettlementList(request: FetchSettlementListRequest): Promise<Pagination<SettlementListItem>> {
    return this.authenticatedRequest('GET', `/api/payment/protected/settlement/list`, request);
  }

  public async createSettlementFileRequest(
    settlementId: number,
    type: 'receipt' | 'transactions'
  ): Promise<RequestInfo> {
    return new Request(this.makeUrl(`/api/payment/protected/settlement/${settlementId}/${type}`).toString(), {
      headers: await this.createAuthorizationHeader(),
      method: 'GET',
    });
  }

  public async makeRequest<T extends object>(request: Request): Promise<T> {
    let response: Response;
    try {
      response = await fetch(request);
    } catch (e) {
      throw new PaymentError(ErrorType.NETWORK);
    }

    if (response.ok) {
      try {
        const body = await response.text();
        return JSON.parse(body, dateReviver) as T;
      } catch (e) {
        console.log(e);
        throw new PaymentError(ErrorType.UNKNOWN);
      }
    } else {
      const error = await this.processError(request, response);
      if (error.errorCode === ErrorType.CONTRACT_VERSION_TOO_LOW) {
        this.onVersionError(error);
      }
      throw error;
    }
  }

  private async createAuthorizationHeader() {
    const idToken = await this.getIdToken();
    if (!idToken) {
      throw new PaymentError(ErrorType.UNAUTHENTICATED, 'ID Token is undefined');
    }
    return {
      Authorization: `Bearer ${idToken}`,
    };
  }

  private async authenticatedRequest<T extends object>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
    path: string,
    queryParams?: any,
    body?: any,
    headers?: Record<string, string>
  ): Promise<T> {
    const h = {
      ...(headers ?? {}),
      ...(await this.createAuthorizationHeader()),
    };

    return this.request(method, path, queryParams, body, h);
  }

  private async request<T extends object>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
    path: string,
    queryParams?: any,
    body?: any,
    headers?: Record<string, string>
  ): Promise<T> {
    const url = this.makeUrl(path);
    if (queryParams) {
      url.search = new URLSearchParams(queryParams).toString();
    }

    const isFormDataBody = body instanceof FormData;
    const combinedHeaders = headers ?? {};
    combinedHeaders['x-contract-version'] = `${this.apiContractVersion}`;

    if (!isFormDataBody) {
      combinedHeaders['Content-Type'] = 'application/json';
    }

    const processedBody = isFormDataBody ? body : JSON.stringify(body);
    const request = new Request(url.toString(), {
      headers: combinedHeaders,
      body: processedBody,
      method,
    });

    return await this.makeRequest(request);
  }

  private makeUrl(path: string): URL {
    return new URL(path, this.baseUrl);
  }

  private async processError(request: Request, response: Response): Promise<PaymentError> {
    let json;
    try {
      json = await response.json();
    } catch (e) {
      return new PaymentError(ErrorType.UNKNOWN);
    }

    if (json.hasOwnProperty('errorCode')) {
      return new PaymentError(json.errorCode, json.devDescription);
    } else {
      return new PaymentError(ErrorType.UNKNOWN);
    }
  }
}
